import { productApi } from '@/api/products';
import { useCurrency } from '@/hooks/useCurrency';
import { Product } from '@/types/api';
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {
    type ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// ── Layout ────────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const PageContent = styled.div`
  padding: 2.5rem 0;
`;

const PageHeader = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BtnPrimary = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
  &:hover {
    background: var(--primary-700);
  }
`;

const BtnSecondary = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: white;
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;
  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.125rem;
    height: 1.125rem;
    color: var(--gray-400);
  }
  input {
    width: 100%;
    padding: 0.5rem 1rem 0.5rem 2.4rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }
  }
`;

const FilterBtn = styled(BtnSecondary)``;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

// ── Stats ─────────────────────────────────────────────────────────────────────

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-sm);
  h3 {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-500);
    margin-bottom: 0.4rem;
  }
  p {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--gray-900);
  }
`;

// ── Table ─────────────────────────────────────────────────────────────────────

const TableCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const ProductsTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: var(--gray-50);
    border-bottom: 1px solid var(--gray-200);
    th {
      padding: 0.75rem 1rem;
      text-align: left;
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--gray-500);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
      &:hover {
        color: var(--gray-800);
      }
      &.right {
        text-align: right;
      }
      &.center {
        text-align: center;
      }
      &.no-sort {
        cursor: default;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--gray-100);
      transition: background 0.1s;
      &:hover {
        background: var(--gray-50);
      }
      &:last-child {
        border-bottom: none;
      }
    }
    td {
      padding: 0.85rem 1rem;
      font-size: 0.875rem;
      color: var(--gray-700);
      &.right {
        text-align: right;
      }
      &.center {
        text-align: center;
      }
    }
  }
`;

const SkuCell = styled.span`
  font-family: monospace;
  font-size: 0.8rem;
  color: var(--gray-500);
`;

const NameCell = styled.span`
  font-weight: 600;
  color: var(--gray-900);
`;

const PriceCell = styled.span`
  font-weight: 600;
  color: var(--gray-900);
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  ${({ $status }) => {
    switch ($status) {
      case 'active':
        return 'background:#d1fae5;color:#065f46;';
      case 'draft':
        return 'background:#f3f4f6;color:#6b7280;';
      case 'archived':
        return 'background:#fee2e2;color:#991b1b;';
      default:
        return 'background:#f3f4f6;color:#6b7280;';
    }
  }}
`;

const ActionCell = styled.td`
  width: 1%;
  white-space: nowrap;
  text-align: right;
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? '#ef4444' : 'var(--gray-400)')};
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    background: ${({ $danger }) => ($danger ? '#fee2e2' : 'var(--gray-100)')};
    color: ${({ $danger }) => ($danger ? '#b91c1c' : 'var(--gray-700)')};
  }
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

// ── Pagination ────────────────────────────────────────────────────────────────

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid var(--gray-200);
  gap: 1rem;
  flex-wrap: wrap;
`;

const PageInfo = styled.div`
  font-size: 0.8rem;
  color: var(--gray-500);
  white-space: nowrap;
`;

const PageSizeSelect = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--gray-500);
  select {
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.375rem;
    font-size: 0.8rem;
    background: white;
    cursor: pointer;
    &:focus {
      outline: none;
      border-color: var(--primary-600);
    }
  }
`;

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.4rem;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--gray-300)')};
  border-radius: 0.375rem;
  background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--gray-700)')};
  font-size: 0.8rem;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${({ $active }) =>
      $active ? 'var(--primary-700)' : 'var(--gray-50)'};
    border-color: ${({ $active }) =>
      $active ? 'var(--primary-700)' : 'var(--gray-400)'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

// ── Empty / loading ───────────────────────────────────────────────────────────

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }
  p {
    color: var(--gray-500);
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
`;

const LoadingRow = styled.tr`
  td {
    padding: 3rem;
    text-align: center;
    color: var(--gray-400);
    font-size: 0.875rem;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZES = [25, 50, 100];

// Visible page numbers around current page
function pageRange(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (
    let p = Math.max(2, current - 1);
    p <= Math.min(total - 1, current + 1);
    p++
  )
    pages.push(p);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}

const ProductCatalog: FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { formatPrice } = useCurrency();

  // ── State ──────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await productApi.getProducts({
        page,
        pageSize,
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setProducts(result.data ?? []);
      setTotal(result.total ?? 0);
      setTotalPages(result.totalPages ?? 1);
    } catch {
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sortBy, sortOrder]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to page 1 when search changes
  const applySearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  // ── Sorting ────────────────────────────────────────────────────────────────
  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const sortIndicator = (col: string) =>
    sortBy === col ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : '';

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = [
      'ID',
      'SKU',
      'Name',
      'Category',
      'Brand',
      'Price',
      'Status',
      'Created',
    ];
    const rows = products.map((p) =>
      [
        p.id,
        p.sku,
        `"${p.name}"`,
        p.category ?? '',
        p.brand ?? '',
        p.basePrice,
        p.status,
        p.createdAt,
      ].join(',')
    );
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], {
      type: 'text/csv',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      alert(`"${file.name}" uploaded — products will be imported.`);
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Stats from currently loaded page (lightweight, real numbers come from API) ──
  const inStock = products.filter((p) => p.status === 'active').length;
  const draft = products.filter((p) => p.status === 'draft').length;
  const archived = products.filter((p) => p.status === 'archived').length;

  // ── Derived pagination ─────────────────────────────────────────────────────
  const firstItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem = Math.min(page * pageSize, total);

  return (
    <PageContainer>
      <PageContent>
        <PageHeader>
          <HeaderTop>
            <Title>Products</Title>
            <HeaderActions>
              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileImport}
              />
              <BtnSecondary onClick={handleImportClick}>
                <ArrowUpTrayIcon /> Import
              </BtnSecondary>
              <BtnSecondary onClick={handleExport}>
                <ArrowDownTrayIcon /> Export
              </BtnSecondary>
              <BtnPrimary onClick={() => navigate('/products/new')}>
                <PlusIcon /> Add Product
              </BtnPrimary>
            </HeaderActions>
          </HeaderTop>

          <ToolbarRow>
            <SearchBox>
              <MagnifyingGlassIcon />
              <input
                type="text"
                placeholder="Search products…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              />
            </SearchBox>
            <FilterBtn onClick={applySearch}>
              <FunnelIcon /> Search
            </FilterBtn>
          </ToolbarRow>
        </PageHeader>

        <Main>
          <StatsGrid>
            <StatCard>
              <h3>Total Products</h3>
              <p>{total}</p>
            </StatCard>
            <StatCard>
              <h3>Active (this page)</h3>
              <p>{inStock}</p>
            </StatCard>
            <StatCard>
              <h3>Draft (this page)</h3>
              <p>{draft}</p>
            </StatCard>
            <StatCard>
              <h3>Archived (this page)</h3>
              <p>{archived}</p>
            </StatCard>
          </StatsGrid>

          <TableCard>
            <ProductsTable>
              <thead>
                <tr>
                  <th onClick={() => handleSort('sku')}>
                    SKU{sortIndicator('sku')}
                  </th>
                  <th onClick={() => handleSort('name')}>
                    Name{sortIndicator('name')}
                  </th>
                  <th onClick={() => handleSort('category')}>
                    Category{sortIndicator('category')}
                  </th>
                  <th onClick={() => handleSort('brand')}>
                    Brand{sortIndicator('brand')}
                  </th>
                  <th className="right" onClick={() => handleSort('basePrice')}>
                    Price{sortIndicator('basePrice')}
                  </th>
                  <th className="center" onClick={() => handleSort('status')}>
                    Status{sortIndicator('status')}
                  </th>
                  <th onClick={() => handleSort('createdAt')}>
                    Created{sortIndicator('createdAt')}
                  </th>
                  <th className="no-sort center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingRow>
                    <td colSpan={8}>Loading…</td>
                  </LoadingRow>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState>
                        <h3>No products found</h3>
                        <p>
                          {search
                            ? 'Try a different search term'
                            : 'Add your first product to get started'}
                        </p>
                        <BtnPrimary onClick={() => navigate('/products/new')}>
                          <PlusIcon /> Add Product
                        </BtnPrimary>
                      </EmptyState>
                    </td>
                  </tr>
                ) : (
                  products.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <SkuCell>{p.sku}</SkuCell>
                      </td>
                      <td>
                        <NameCell>{p.name}</NameCell>
                        {p.description && (
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: 'var(--gray-400)',
                              marginTop: '0.1rem',
                            }}
                          >
                            {p.description.slice(0, 60)}
                            {p.description.length > 60 ? '…' : ''}
                          </div>
                        )}
                      </td>
                      <td>{p.category ?? '—'}</td>
                      <td>{p.brand ?? '—'}</td>
                      <td className="right">
                        <PriceCell>{formatPrice(p.basePrice)}</PriceCell>
                      </td>
                      <td className="center">
                        <StatusBadge $status={p.status}>{p.status}</StatusBadge>
                      </td>
                      <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                      <ActionCell>
                        <IconBtn
                          title="Edit"
                          onClick={() => navigate(`/products/edit/${p.id}`)}
                        >
                          <PencilSquareIcon />
                        </IconBtn>
                        <IconBtn
                          $danger
                          title="Delete"
                          onClick={() => {
                            if (confirm(`Delete "${p.name}"?`))
                              productApi
                                .deleteProduct(p.id)
                                .then(fetchProducts);
                          }}
                        >
                          <TrashIcon />
                        </IconBtn>
                      </ActionCell>
                    </tr>
                  ))
                )}
              </tbody>
            </ProductsTable>

            {!loading && total > 0 && (
              <PaginationBar>
                <PageSizeSelect>
                  Rows per page:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </PageSizeSelect>

                <PageInfo>
                  {firstItem}–{lastItem} of {total.toLocaleString()}
                </PageInfo>

                <PageButtons>
                  <PageBtn
                    title="Previous page"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeftIcon />
                  </PageBtn>
                  {pageRange(page, totalPages).map((p, i) =>
                    p === '…' ? (
                      <span
                        key={`ellipsis-${i}`}
                        style={{
                          padding: '0 0.25rem',
                          color: 'var(--gray-400)',
                        }}
                      >
                        …
                      </span>
                    ) : (
                      <PageBtn
                        key={p}
                        $active={p === page}
                        onClick={() => setPage(p as number)}
                      >
                        {p}
                      </PageBtn>
                    )
                  )}
                  <PageBtn
                    title="Next page"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRightIcon />
                  </PageBtn>
                </PageButtons>
              </PaginationBar>
            )}
          </TableCard>
        </Main>
      </PageContent>
    </PageContainer>
  );
};

export default ProductCatalog;
